<template>
    <div class="dragged-cell">
        <v-group
            __use-strict-mode
            data-qa="cell-group"
            data-type="cell-group"
            :x="cell.mxGeometry.x + offset.x"
            :y="cell.mxGeometry.y + offset.y"
        >
            <KonvaCampaignCellBigCard
                v-if="shouldShowBigCard && !isNote"
                :cell="cell"
                :border-color="borderColor"
                :enabled-integrations="enabledIntegrations"
                :big-card-cells="bigCardDisplayCells"
            />

            <v-group
                v-else-if="imageUrlFromCellType"
            >
                <v-group>
                    <v-rect :config="configCellSquareContainer" />

                    <KonvaCampaignImage
                        :config="configCellIcon"
                        :src="imageUrlFromCellType"
                    />

                    <KonvaCampaignImage
                        v-if="statusIndicatorFromCell"
                        :src="statusIndicatorFromCell"
                        :config="configCellStatusIndicator"
                    />

                    <KonvaCampaignImage
                        v-if="isLockedSequence"
                        :config="configCellLockedIndicator"
                        :src="imagePath.lockedSequence"
                    />
                </v-group>
            </v-group>

            <v-rect v-else :config="configCellRectangle" />

            <v-text
                v-if="showText"
                data-qa="cell-label"
                :config="configCellText"
            />
        </v-group>
    </div>
</template>

<script>

import KonvaCampaignImage from './KonvaCampaignImage.vue';
import KonvaCampaignCellBigCard from './KonvaCampaignCellBigCard.vue';

import {
    DECISION_TYPE,
    FLOW_TYPE,
    MAIN_CANVAS_ID,
    SEQUENCE_START,
    NOTES_TYPE,
    INTEGRATION_TRIGGER_TYPE,
    INTEGRATION_ACTION_TYPE,
    GRID_SIZE,
    BIG_CARD_WIDTH,
} from '../utils/editor.constants';
import { getIconPathByCellType } from '../utils/cell/automation-display-utils';

const BLACK = '#000';
const WHITE = '#fff';
const TRANSPARENT = 'transparent';

export default {
    components: {
        KonvaCampaignImage,
        KonvaCampaignCellBigCard,
    },
    props: {
        offset: {
            type: Object,
            required: true,
        },
        shouldShowBigCard: {
            type: Boolean,
            default: false,
        },
        enabledIntegrations: {
            type: Array,
            default: () => [],
        },
        cell: {
            type: Object,
            required: true,
        },
        allCells: {
            type: Array,
            default: () => [],
        },
        selectedSequence: {
            type: String,
            default: '1',
        },
    },

    data() {
        return {
            imagePath: {
                connector: '/resources/funnel/images/svg/connectors/default.svg',
                connectorHover: '/resources/funnel/images/svg/connectors/hover.svg',
                lockedSequence: '/resources/funnel/images/svg/lock.svg',
                redStatusIndicator: '/resources/funnel/images/svg/cell-icon/status-indicator/red.svg',
                yellowStatusIndicator: '/resources/funnel/images/svg/cell-icon/status-indicator/yellow.svg',
            },
        };
    },

    computed: {
        bigCardDisplayCells() {
            return this.cell?.elementChildren.length
                ? this.cell.elementChildren
                : [this.cell];
        },

        isBroken() {
            return this.cell?.value.broken === '1';
        },

        isReady() {
            return this.cell?.value?.ready === '1';
        },

        isPublished() {
            return this.cell?.value?.published === '1';
        },

        cellType() {
            return this.cell.type;
        },

        borderColor() {
            return TRANSPARENT;
        },

        configCellIcon() {
            if (this.isDecisionDiamond) {
                return {
                    width: GRID_SIZE,
                    height: GRID_SIZE,
                    listening: false,
                };
            }

            return {
                x: GRID_SIZE / 4,
                y: GRID_SIZE / 4,
                width: GRID_SIZE / 2,
                height: GRID_SIZE / 2,
                listening: false,
            };
        },

        configCellStatusIndicator() {
            return {
                x: 36,
                y: -4,
                width: 16,
                height: 16,
                listening: false,
            };
        },

        configCellLockedIndicator() {
            return {
                x: 54,
                y: 16,
                width: 16,
                height: 16,
                listening: false,
            };
        },

        configCellSquareContainer() {
            let { width, height } = this.cell.mxGeometry;

            if (!this.isNote) {
                width = GRID_SIZE;
                height = GRID_SIZE;
            }

            if (this.shouldShowBigCard && !this.isNote) {
                width = BIG_CARD_WIDTH;
                height = (this.bigCardDisplayCells.length + 1) * GRID_SIZE;
            }

            return {
                fill: this.isDecisionDiamond
                    ? TRANSPARENT
                    : WHITE,
                shadowColor: 'black',
                stroke: this.borderColor,
                strokeWidth: 3,
                shadowOffset: { x: 0, y: 2 },
                shadowOpacity: 0.5,
                cornerRadius: 12,
                width,
                height,
                shadowForStrokeEnabled: false,
                perfectDrawEnabled: false,
            };
        },

        configCellRectangle() {
            return {
                stroke: '#555',
                strokeWidth: 2,
                fill: WHITE,
                cornerRadius: 5,
                width: this.cell.mxGeometry.width,
                height: this.cell.mxGeometry.height,
                perfectDrawEnabled: false,
                listening: false,
                shadowForStrokeEnabled: false,
            };
        },

        configCellText() {
            const textLabel = this.cell.value.name.replaceAll('~br~', '\n');

            return {
                text: textLabel,
                width: 200,
                x: -76,
                y: 65,
                fill: BLACK,
                align: 'center',
                fontSize: 14,
                fontFamily: 'Proxima Nova',
            };
        },

        isPartOfSequence() {
            return this.selectedSequence !== '1';
        },

        isSequence() {
            return this.cellType === FLOW_TYPE;
        },

        isLockedSequence() {
            return this.isSequence
                    && this.isMainCanvas
                    && this.cell?.value?.methodLocked === '1';
        },

        isMainCanvas() {
            return this.selectedSequence === MAIN_CANVAS_ID;
        },

        isDecisionDiamond() {
            return this.cellType === DECISION_TYPE;
        },

        isNote() {
            return this.cellType === NOTES_TYPE;
        },

        statusIndicatorFromCell() {
            if (this.cellType === SEQUENCE_START || this.isNote) return null;

            if (this.isReady && !this.isBroken) {
                if (this.isPublished) return null;

                return this.imagePath.yellowStatusIndicator;
            }

            return this.imagePath.redStatusIndicator;
        },

        imageUrlFromCellType() {
            let iconColor;

            if (this.cellType === INTEGRATION_TRIGGER_TYPE) {
                return this.enabledIntegrations.find((integration) => {
                    return this.cell.value.integrationTriggerId === integration.id;
                })?.iconUrl;
            }

            if (this.cellType === INTEGRATION_ACTION_TYPE) {
                return this.enabledIntegrations.find((integration) => {
                    return this.cell.value.integrationActionId === integration.id;
                })?.iconUrl;
            }

            if (this.isBroken) iconColor = 'red';
            else if (this.cellType !== NOTES_TYPE) {
                if (this.isSequence || this.isDecisionDiamond || this.isPartOfSequence) iconColor = 'blue';
                else iconColor = 'green';
            } else iconColor = 'neutral';

            return getIconPathByCellType(this.cellType, iconColor);
        },

        showText() {
            const {
                cell,
                isEditingName,
                shouldShowBigCard,
            } = this;

            return cell.value.name && !isEditingName && !shouldShowBigCard;
        },

    },

    methods: {},
};
</script>
